<template>
    <div>
        <b-card class="detail-card mb-2 compare-info-card" header-class="compare-info-card-header"
            header-text-variant="white">
            <template #header>
                <h4 class="font-weight-bold text-center mb-1 compare-name-text">
                    {{ `${point.name}` }}
                </h4>
                <div class="text-center compare-address-text">
                    {{ `(${point.lat}, ${point.long})` }}
                </div>
            </template>
            <b-row class="no-gutters align-items-center compare-body-row">
                <b-col cols="5" class="text-center">
                    <h1 class="mb-0 font-weight-bold compare-last-value-text" v-if="lastValueText">
                        {{ lastValueText }}
                    </h1>
                </b-col>
                <b-col cols="2" class="text-center">
                    <b-img v-if="legendIconUrl" fluid class="compare-legend-icon" :src="legendIconUrl" />
                </b-col>
                <b-col cols="5" class="text-center">
                    <div class="compare-legend-text" v-if="lastLegendText">
                        {{ lastLegendText }}
                    </div>
                </b-col>
            </b-row>
            <div class="mt-1 text-center compare-update-text" v-if="lastUpdateText">
                Cập nhật lúc: {{ lastUpdateText }}
            </div>
        </b-card>
        <b-card class="detail-card mb-0" header-class="rainValueGather10-card-bg" header-text-variant="white">
            <div class="mb-2" v-if="!isCompareHistory">
                Chọn điểm khác để so sánh:
                <v-select :options="listOtherPoint" :clearable="false" :multiple="true" :filterable="false"
                    :searchable="false" label="name" v-model="selectedLayerPoint"></v-select>
            </div>
            <div class="mb-2">
                <b-form-checkbox v-model="isCompareHistory">
                    So sánh với lịch sử
                </b-form-checkbox>
            </div>
            <div style="display: flex; justify-content: space-between;">
                <DatePicker v-model="range" range lang="vi" format="DD/MM/YYYY" name="time" :shortcuts="shortcuts">
                </DatePicker>
                <v-select style="width: 240px;" v-model="type" class="ml-2" :options="typeOptions" :clearable="false"
                    :filterable="false" :searchable="false"></v-select>
            </div>
            <b-overlay :show="isLoading" spinner-variant="primary" variant="light" opacity="0.5" blur="1px"
                rounded="sm">
                <b-row class="mt-2">
                    <b-col cols="12" v-for="(item, index) in listMap" :key="item.name">
                        <apex-chart v-if="!isCompareHistory" :key="`point-${item.name}`" :type="chartType" :height="320"
                            :options="pointChartOptions(item.name, index)" :series="pointChartSeries(index)" />
                        <apex-chart v-else :key="`history-${item.name}`" type="line" :height="320"
                            :options="historyChartOptions(item.name, index)" :series="historyChartSeries(index)" />
                    </b-col>
                </b-row>
            </b-overlay>
        </b-card>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment-timezone';
import API_CONFIG from '@/api-config';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/vi';
import { getRange } from '../utils/datetime';

export default {
    components: {
        DatePicker
    },
    async mounted() {
        await this.$nextTick();
        this.typeOptions = await this.getDataType();
        if (this.typeOptions.length === 0) return;
        this.type = this.typeOptions[0];
        this.range = this.getCurrentYearRange();
        this.getData();
    },
    props: {
        point: {
            type: Object,
            required: true
        },
        listLayerPoint: {
            type: Array,
            required: true
        },
    },
    computed: {
        listOtherPoint() {
            return this.listLayerPoint.filter(item => item.id !== this.point.id);
        },
        mainSeries() {
            const series = this.listStatistic?.[0]?.[0]?.data;
            return Array.isArray(series) ? series : [];
        },
        lastValueText() {
            const value = this.mainSeries[this.mainSeries.length - 1]?.value;
            return this.formatValue(value);
        },
        lastValueNumber() {
            if (this.mainSeries.length === 0) return null;
            const value = Number(this.mainSeries[this.mainSeries.length - 1]?.value);
            if (Number.isNaN(value)) return null;
            return value;
        },
        legend() {
            if (Array.isArray(this.point?.legend) && this.point.legend.length > 0) return this.point.legend;
            if (Array.isArray(this.point?.layer?.legend) && this.point.layer.legend.length > 0) return this.point.layer.legend;
            return [];
        },
        matchedLegendItem() {
            if (this.legend.length === 0 || this.lastValueNumber == null) return null;
            for (const item of this.legend) {
                if (this.lastValueNumber <= item.value) return item;
            }
            return this.legend[this.legend.length - 1] || null;
        },
        lastLegendText() {
            const item = this.matchedLegendItem;
            if (!item) return '';
            return item.name || '';
        },
        legendIconUrl() {
            const icon = this.matchedLegendItem?.icon;
            if (!icon) return '';
            if (icon.startsWith('http://') || icon.startsWith('https://')) return icon;
            return API_CONFIG.BASE_URL + 'download?fileName=' + icon;
        },
        chartType() {
            return this.point?.chartType === 'bar' ? 'bar' : 'line';
        },
        lastUpdateText() {
            if (this.mainSeries.length === 0) return '';
            const lastItem = this.mainSeries[this.mainSeries.length - 1];
            if (lastItem?.time) return moment(lastItem.time).format('DD/MM/YYYY HH:mm');
            return lastItem?.label || '';
        }
    },
    data: function () {
        return {
            shortcuts: [
                {
                    text: 'Năm nay',
                    onClick: () => {
                        const now = new Date();
                        const year = now.getFullYear();
                        const start = new Date(year, 0, 1, 0, 0, 0, 0);
                        const end = new Date(year, 11, 31, 23, 59, 59, 999);
                        return [start, end];
                    }
                },
                {
                    text: '1 tuần qua',
                    onClick() {
                        return getRange(7);
                    }
                },
                {
                    text: '2 tuần qua',
                    onClick() {
                        return getRange(14);
                    }
                },
                {
                    text: '1 tháng qua',
                    onClick() {
                        return getRange(31);
                    }
                },
                {
                    text: '2 tháng qua',
                    onClick() {
                        return getRange(61);
                    }
                },
                {
                    text: '3 tháng qua',
                    onClick() {
                        return getRange(92);
                    }
                },
                {
                    text: '6 tháng qua',
                    onClick() {
                        return getRange(183);
                    }
                },
                {
                    text: '1 năm qua',
                    onClick() {
                        return getRange(365);
                    }
                },
                {
                    text: '5 năm qua',
                    onClick() {
                        return getRange(5 * 365);
                    }
                }
            ],
            range: [],
            unit: '',
            typeOptions: [],
            type: null,
            isCompareHistory: false,
            isLoading: false,
            listMap: [
                {
                    name: 'Trung bình ngày',
                    value: 'flex'
                }
            ],
            listStatistic: [[]],
            selectedLayerPoint: []
        };
    },
    watch: {
        range() {
            this.getData();
        },
        type(value) {
            this.getData();
        },
        isCompareHistory() {
            if (this.isCompareHistory) {
                this.selectedLayerPoint = [];
            }
            this.getData();
        },
        selectedLayerPoint() {
            if (this.isCompareHistory) return;
            this.getData();
        }
    },
    methods: {
        ...mapActions({
            getStatisticFlex: 'statistic/flex',
            getStatisticByHistory: 'statistic/byHistory',
            getDataType: 'data/getDataType'
        }),
        async getData() {
            if (this.isLoading) return;
            this.isLoading = true;
            this.listStatistic = [[]];
            if (this.isCompareHistory) {
                const historySeries = await this.getHistorySeries();
                for (let i = 0; i < this.listMap.length; i++) {
                    this.listStatistic[i] = historySeries;
                }
            } else {
                const listPoint = [this.point, ...this.selectedLayerPoint];
                for (const point of listPoint) {
                    const listData = await this.getDataPoint(point.id);
                    for (let i = 0; i < this.listMap.length; i++) {
                        this.listStatistic[i].push({
                            name: point.name,
                            data: listData[i]
                        });
                    }
                }
            }
            this.isLoading = false;
        },
        buildStatisticParams(pointId = this.point.id) {
            const params = { pointId, type: this.type };
            if (this.range.length === 2) {
                params.from = this.range[0];
                params.to = this.range[1];
            }
            return params;
        },
        async getDataPoint(pointId) {
            try {
                const params = this.buildStatisticParams(pointId);
                return await Promise.all([this.getStatisticFlex(params)]);
            } catch (error) { }
        },
        async getHistorySeries() {
            try {
                const params = this.buildStatisticParams(this.point.id);
                const listData = await this.getStatisticByHistory(params);
                return this.convertHistoryToSeries(listData);
            } catch (error) {
                return [];
            }
        },
        convertHistoryToSeries(listData) {
            if (!Array.isArray(listData) || listData.length === 0) return [];
            const sortedData = [...listData].sort((a, b) => {
                const timeA = this.getLabelTimestamp(a?.label);
                const timeB = this.getLabelTimestamp(b?.label);
                if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
                if (Number.isNaN(timeA)) return 1;
                if (Number.isNaN(timeB)) return -1;
                return timeA - timeB;
            });
            const yearSet = new Set();
            sortedData.forEach(item => {
                if (!item || typeof item !== 'object') return;
                Object.keys(item).forEach(key => {
                    if (/^\d{4}$/.test(key)) yearSet.add(key);
                });
            });
            const summarySeries = [
                {
                    name: 'Min',
                    data: sortedData.map(item => ({
                        label: item?.label,
                        value: this.toNumberOrNull(item?.min)
                    }))
                },
                {
                    name: 'Max',
                    data: sortedData.map(item => ({
                        label: item?.label,
                        value: this.toNumberOrNull(item?.max)
                    }))
                },
                {
                    name: 'AVG',
                    data: sortedData.map(item => ({
                        label: item?.label,
                        value: this.toNumberOrNull(item?.avg)
                    }))
                }
            ];
            const years = Array.from(yearSet).sort((a, b) => Number(a) - Number(b));
            const yearSeries = years.map(year => ({
                name: year,
                data: sortedData.map(item => ({
                    label: item?.label,
                    value: this.toNumberOrNull(item?.[year])
                }))
            }));
            return [...summarySeries, ...yearSeries];
        },
        toNumberOrNull(value) {
            if (value == null || value === '') return null;
            const numberValue = Number(value);
            return Number.isFinite(numberValue) ? numberValue : null;
        },
        pointChartSeries(index) {
            return this.buildChartSeries(index, false);
        },
        historyChartSeries(index) {
            return this.buildChartSeries(index, true);
        },
        pointChartOptions(title, index) {
            return this.buildChartOptions(title, index, false);
        },
        historyChartOptions(title, index) {
            return this.buildChartOptions(title, index, true);
        },
        buildChartSeries(index, isHistory = this.isCompareHistory) {
            const series = this.listStatistic?.[index] || [];
            if (isHistory) {
                return series.map((item, sourceIndex) => {
                    const valueByLabel = new Map();
                    (item.data || []).forEach(dataItem => {
                        if (dataItem?.value == null || !dataItem?.label) return;
                        valueByLabel.set(dataItem.label, dataItem.value);
                    });
                    const categories = this.getChartCategories(index);
                    return {
                        name: item.name,
                        segment: 'history',
                        sourceIndex,
                        data: categories.map(label => valueByLabel.has(label) ? valueByLabel.get(label) : null)
                    };
                });
            }
            const formattedSeries = [];
            const categories = this.getChartCategories(index);
            const now = Date.now();
            series.forEach((item, sourceIndex) => {
                const valueByLabel = new Map();
                const futureByLabel = new Map();
                (item.data || []).forEach(dataItem => {
                    if (dataItem?.value == null || !dataItem?.label) return;
                    valueByLabel.set(dataItem.label, dataItem.value);
                    futureByLabel.set(dataItem.label, this.isFutureDataPoint(dataItem, now));
                });

                const pastData = categories.map(label => {
                    if (!valueByLabel.has(label)) return null;
                    if (futureByLabel.get(label)) return null;
                    return valueByLabel.get(label);
                });
                const futureData = categories.map(label => {
                    if (!valueByLabel.has(label)) return null;
                    if (!futureByLabel.get(label)) return null;
                    return valueByLabel.get(label);
                });

                if (this.chartType === 'line') {
                    const firstFutureIndex = futureData.findIndex(value => value != null);
                    if (firstFutureIndex > 0 && pastData[firstFutureIndex - 1] != null) {
                        futureData[firstFutureIndex - 1] = pastData[firstFutureIndex - 1];
                    }
                }

                if (pastData.some(value => value != null)) {
                    formattedSeries.push({
                        name: `${item.name}__past`,
                        segment: 'past',
                        displayName: item.name,
                        sourceIndex,
                        data: pastData
                    });
                }
                if (futureData.some(value => value != null)) {
                    formattedSeries.push({
                        name: `${item.name}__future`,
                        segment: 'future',
                        displayName: item.name,
                        sourceIndex,
                        data: futureData
                    });
                }
            });
            return formattedSeries;
        },
        buildChartOptions(title, index, isHistory = this.isCompareHistory) {
            const series = this.buildChartSeries(index, isHistory);
            const categories = this.getChartCategories(index);
            const historyStyles = this.getHistoryStyles(series);
            const colors = isHistory
                ? historyStyles.colors
                : series.map(item => item.segment === 'future' ? '#d32f2f' : this.seriesColor(item.sourceIndex));
            return {
                chart: {
                    id: `${isHistory ? 'compare-history' : 'compare-point'}-${title}`,
                    toolbar: {
                        tools: {
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            reset: false,
                            pan: false
                        }
                    }
                },
                colors,
                stroke: {
                    curve: 'smooth',
                    width: isHistory ? historyStyles.widths : 2,
                    dashArray: isHistory ? historyStyles.dashArray : series.map(item => item.segment === 'future' ? 6 : 0)
                },
                markers: {
                    size: isHistory ? 0 : 3,
                    hover: {
                        size: 5
                    }
                },
                dataLabels: {
                    enabled: false
                },
                plotOptions: !isHistory && this.chartType === 'bar' ? {
                    bar: {
                        columnWidth: '60%'
                    }
                } : {},
                fill: !isHistory && this.chartType === 'bar' ? {
                    type: series.map(item => item.segment === 'future' ? 'pattern' : 'solid'),
                    opacity: 1,
                    pattern: {
                        style: series.map(item => item.segment === 'future' ? 'slantedLines' : 'verticalLines'),
                        width: 6,
                        height: 6,
                        strokeWidth: 2
                    }
                } : {},
                legend: isHistory ? {
                    show: true,
                    onItemClick: {
                        toggleDataSeries: true
                    },
                    onItemHover: {
                        highlightDataSeries: true
                    }
                } : {
                    show: true,
                    formatter: seriesName => seriesName?.replace(/__(past|future)$/, '') || '',
                    onItemClick: {
                        toggleDataSeries: true
                    },
                    onItemHover: {
                        highlightDataSeries: true
                    }
                },
                grid: {
                    yaxis: {
                        lines: {
                            show: false
                        }
                    }
                },
                title: {
                    text: title,
                    align: 'center'
                },
                xaxis: {
                    categories,
                    labels: {
                        rotate: 0,
                        hideOverlappingLabels: true,
                        formatter: (value, _timestamp, opts) => {
                            return this.formatXAxisLabel(value, opts?.dataPointIndex, categories);
                        }
                    }
                },
                yaxis: {
                    labels: {
                        formatter: value => this.formatValue(value)
                    }
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    custom: ({ series: tooltipSeries, dataPointIndex, w }) => {
                        if (dataPointIndex == null || dataPointIndex < 0) return '';
                        const category = categories?.[dataPointIndex] || w?.globals?.labels?.[dataPointIndex] || '';
                        const rows = [];
                        const seriesNames = w?.globals?.seriesNames || [];
                        const colors = w?.globals?.colors || [];
                        for (let seriesIndex = 0; seriesIndex < tooltipSeries.length; seriesIndex++) {
                            const value = tooltipSeries?.[seriesIndex]?.[dataPointIndex];
                            if (value == null || Number.isNaN(value)) continue;
                            const rawName = seriesNames[seriesIndex] || '';
                            const name = rawName.replace(/__(past|future)$/, '');
                            const color = colors[seriesIndex] || '#999999';
                            rows.push(
                                `<div class="compare-tooltip-row">
                                    <span class="compare-tooltip-dot" style="background:${color}"></span>
                                    <span class="compare-tooltip-name">${name}</span>
                                    <span class="compare-tooltip-value">${this.formatValue(value)}</span>
                                </div>`
                            );
                        }
                        if (rows.length === 0) return '';
                        return `<div class="compare-tooltip">
                            <div class="compare-tooltip-title">${category}</div>
                            ${rows.join('')}
                        </div>`;
                    }
                }
            };
        },
        isFutureDataPoint(dataItem, now) {
            if (!dataItem) return false;
            const parsedLabel = moment(dataItem.label, 'D/M/YYYY', true);
            const timestamp = parsedLabel.isValid() ? parsedLabel.valueOf() : Number.NaN;
            if (Number.isNaN(timestamp)) return false;
            return timestamp >= now;
        },
        seriesColor(index) {
            const palette = ['#008FFB', '#00E396', '#775DD0', '#FEB019', '#546E7A', '#26A69A', '#D10CE8', '#F86624'];
            return palette[index % palette.length];
        },
        getHistoryStyles(series) {
            const colorByName = {
                Min: '#ff6d00',
                AVG: '#9e9d24',
                Max: '#8e245e'
            };
            const yearPalette = ['#d84315', '#7b1fa2', '#455a64', '#00897b', '#3949ab', '#6d4c41', '#1e88e5', '#2e7d32', '#ad1457', '#5d4037'];
            const yearColorMap = {};
            let yearColorIndex = 0;
            const colors = [];
            const widths = [];
            const dashArray = [];
            series.forEach(item => {
                const name = item?.name || '';
                if (Object.prototype.hasOwnProperty.call(colorByName, name)) {
                    colors.push(colorByName[name]);
                    widths.push(3);
                    dashArray.push(0);
                    return;
                }
                if (!yearColorMap[name]) {
                    yearColorMap[name] = yearPalette[yearColorIndex % yearPalette.length];
                    yearColorIndex += 1;
                }
                colors.push(yearColorMap[name]);
                widths.push(2);
                dashArray.push(0);
            });
            return { colors, widths, dashArray };
        },
        getLabelTimestamp(label) {
            const parsed = moment(label, 'D/M/YYYY', true);
            if (!parsed.isValid()) return Number.NaN;
            return parsed.valueOf();
        },
        getChartCategories(index) {
            const series = this.listStatistic?.[index] || [];
            const categories = [];
            const seen = new Set();
            series.forEach(item => {
                (item.data || []).forEach(dataItem => {
                    if (dataItem?.value == null || !dataItem?.label || seen.has(dataItem.label)) return;
                    seen.add(dataItem.label);
                    categories.push(dataItem.label);
                });
            });
            return categories;
        },
        formatXAxisLabel(value, dataPointIndex, categories) {
            const parsed = moment(value, 'D/M/YYYY', true);
            if (!parsed.isValid()) return value;
            const dayMonth = `${parsed.date()}/${parsed.month() + 1}`;
            const full = `${dayMonth}/${parsed.year()}`;
            let index = dataPointIndex;
            if (index == null || index < 0) {
                index = Array.isArray(categories) ? categories.indexOf(value) : -1;
            }
            if (index <= 0) return full;

            const prevValue = categories?.[index - 1];
            const prevParsed = moment(prevValue, 'D/M/YYYY', true);
            if (!prevParsed.isValid()) return full;
            if (prevParsed.year() !== parsed.year()) return full;
            return dayMonth;
        },
        getCurrentYearRange() {
            const now = new Date();
            const year = now.getFullYear();
            const start = new Date(year, 0, 1, 0, 0, 0, 0);
            const end = new Date(year, 11, 31, 23, 59, 59, 999);
            return [start, end];
        },
        formatValue(value) {
            return value?.toFixed(2) ?? '';
        }
    }
};
</script>
<style>
.mx-datepicker-sidebar {
    width: 120px;
}

.v-select .vs__selected-options {
    flex-wrap: nowrap;
    white-space: nowrap;
    overflow: hidden;
}

.compare-last-value-text {
    font-size: 3.4rem;
    line-height: 1.1;
}

.compare-body-row {
    min-height: 82px;
}

.compare-legend-icon {
    width: 68px;
    height: 68px;
    object-fit: contain;
}

.compare-legend-text {
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
}

.compare-name-text {
    font-size: 1rem;
}

.compare-address-text {
    font-size: 0.875rem;
}

.compare-update-text {
    font-size: 0.8rem;
    opacity: 0.95;
}

.compare-info-card {
    background-color: #4a90d9;
    color: #fff;
}

.compare-info-card .card-header {
    background-color: #3378c0;
    color: #fff;
    border-bottom: 0;
}

.compare-tooltip {
    padding: 8px 10px;
}

.compare-tooltip-title {
    font-weight: 600;
    margin-bottom: 4px;
}

.compare-tooltip-row {
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1.3;
}

.compare-tooltip-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    display: inline-block;
}

.compare-tooltip-name {
    flex: 1;
}

.compare-tooltip-value {
    font-weight: 600;
}
</style>
